import {useEffect, useMemo, useState} from "react";
import {Box, useMediaQuery} from "@mui/material";
import {useQuery} from "react-query";
import ReadCanvasView from "./ReadCanvasView";
import {
    ResourceService
} from "crqlar-react-common/dist/services/resource/resource.service";
import {Resource} from "crqlar-react-common/dist/models/resource/resource.model";
import {
    selectRestaurantToFloorPlanElements,
    selectTablesToFloorPlanItems,
} from "./utils/migration-utils";
import {
    endOfDay,
    formatDateLocalISO,
    startOfDay
} from "crqlar-react-common/dist/utils/date.util";
import {
    FloorplanService
} from "crqlar-react-common/dist/services/floorplan/floorplan.service";
import LoadingSkeleton from "./shared/LoadingSkeleton";
import {groupFloorplanReservations, groupHalfboardProfiles} from "./utils/reservation-utils";
import {useOrganization} from "crqlar-react-common/dist/state/auth.reducer";
import {
    ReservationStatus
} from "crqlar-react-common/dist/models/reservation/reservation.model";
import {
    useCurrentAppId
} from "crqlar-react-common/dist/utils/hooks/app/useOrganizationApps";
import {
    ReservationActions,
    useReservationsState
} from "../../../../store/reservations.reducer";
import {useReadStore} from "./hooks/useReadStore";
import {FloorplanContextProvider} from "./FloorplanContextProvider";
import {useDispatch, useSelector} from "react-redux";
import {getStoredTableGroups} from "./utils/table-groups";

interface Props {
    restaurant: Resource;
    date: Date;
    desktopReservationsCollapsible?: boolean;
}


const FloorPlanContainer: React.FC<Props> = ({
    restaurant,
    date,
    desktopReservationsCollapsible = false,
}) => {
    const isMobile = useMediaQuery("(max-width:900px)");
    const appId = useCurrentAppId();
    const organization = useOrganization();
    const {selectedStatuses, searchQuery} = useReservationsState(appId);

    const dispatch = useDispatch()

    const searchBarOpenTriggered = useSelector((state: any) => state.action.searchBarTriggered);

    useEffect(() => {
        // If the reset trigger was executed, we need to reset the search query and use redux dispatch for changing the state
        if (!searchBarOpenTriggered) {
            dispatch({
                type: ReservationActions.SET_RESERVATIONS,
                appId,
                payload: {
                    searchQuery: undefined
                },
            })
        }
    }, [searchBarOpenTriggered])

    const restaurantId = restaurant.id!;
    const restaurantElements = useMemo(
        () => selectRestaurantToFloorPlanElements(restaurant),
        [restaurant],
    );
    const tableGroups = useMemo(
        () => getStoredTableGroups(restaurant.meta as Record<string, any> | undefined),
        [restaurant.meta],
    );

    const patch = useReadStore(s => s.patch)

    const query = searchQuery?.trim() || "";
    const status = (selectedStatuses ?? []) as ReservationStatus[];
    const statusKey = [...status].sort().join("-");
    const dayKey = formatDateLocalISO(date);

    const tablesQuery = useQuery({
        queryKey: ["floorplanTables", restaurantId],
        queryFn: async () => {
            const response = await ResourceService.getAllResources({
                parentIds: [restaurantId],
                limit: {
                    offset: 0,
                    limit: 1000,
                    sort: [{field: "name", direction: "ASC"}],
                },
            });
            return response.content || [];
        },
        select: selectTablesToFloorPlanItems,
    });

    const assignedQuery = useQuery({
        queryKey: ["floorplanAssigned", restaurantId, dayKey, statusKey, query],
        queryFn: async () => {
            return await FloorplanService.getAssignedReservations({
                restaurantId,
                limit: {
                    offset: 0,
                    limit: 1000,
                    sort: [{field: "expectedStartDate", direction: "ASC"}],
                },
                timespan: {
                    start: startOfDay(date),
                    end: endOfDay(date),
                },
                status: status,
                query: query || undefined,
            });
        },
        keepPreviousData: !query,
    });

    const unassignedQuery = useQuery({
        queryKey: ["floorplanUnassigned", restaurantId, dayKey, statusKey, query],
        queryFn: async () => {
            return await FloorplanService.getUnassignedReservations({
                restaurantId,
                limit: {
                    offset: 0,
                    limit: 1000,
                    sort: [{field: "expectedStartDate", direction: "ASC"}],
                },
                timespan: {
                    start: startOfDay(date),
                    end: endOfDay(date),
                },
                status: status,
                query: query || undefined,
            });
        },
        keepPreviousData: !query,
    });

    const halfboardProfilesQuery = useQuery({
        queryKey: ["floorplanHalfboardProfiles", restaurantId, dayKey, query],
        queryFn: async () => {
            return await FloorplanService.getHalfboards({
                restaurantId,
                limit: {
                    offset: 0,
                    limit: 1000,
                    sort: [{field: "expectedStartDate", direction: "ASC"}],
                },
                timespan: {
                    start: startOfDay(date),
                    end: endOfDay(date),
                },
                query: query || undefined,
            });
        },
        keepPreviousData: !query,
    });


    const tables = tablesQuery.data ?? [];
    const assignedReservations = assignedQuery.data || [];
    const unassignedReservations = unassignedQuery.data || [];
    const halfboardProfiles = halfboardProfilesQuery.data || [];

    const showSeatsAroundTable = restaurant.meta?.seatsAroundTable ?? false;

    const elements = useMemo(
        () => [...restaurantElements, ...tables]
            .sort((a, b) => {
                const aZ = a.element?.canvas.zIndex ?? a.table?.canvas.zIndex ?? 0;
                const bZ = b.element?.canvas.zIndex ?? b.table?.canvas.zIndex ?? 0;
                return aZ - bZ;
            }),
        [restaurantElements, tables],
    );

    const isLoading = tablesQuery.isLoading;
    const isSuccess = tablesQuery.isSuccess && assignedQuery.isSuccess && unassignedQuery.isSuccess && halfboardProfilesQuery.isSuccess;

    // Show skeleton for at least 500ms on first load / restaurant switch
    const [minTimeElapsed, setMinTimeElapsed] = useState(false);
    useEffect(() => {
        setMinTimeElapsed(false);
        const timer = setTimeout(() => setMinTimeElapsed(true), 500);
        return () => clearTimeout(timer);
    }, [restaurantId]);

    useEffect(() => {
        if (!isSuccess) return
        patch({
            assignedReservations: groupFloorplanReservations(assignedReservations),
            unassignedReservations: groupFloorplanReservations(unassignedReservations),
            halfboardProfiles: groupHalfboardProfiles(halfboardProfiles, organization?.agenciesWithIndividualReservations),
        });
    }, [isSuccess, assignedReservations, unassignedReservations, halfboardProfiles, patch]);


    if (isLoading || !minTimeElapsed) {
        return (<LoadingSkeleton isMobile={isMobile}/>);
    }

    return (
        <FloorplanContextProvider
            mode="read"
            showSeatsAroundTable={showSeatsAroundTable}
            restaurantId={restaurantId}
            restaurantName={restaurant.name ?? ''}
            restaurantDraft={restaurant}
        >
            <Box
                sx={{
                    height: "100%",
                    width: "100%",
                    display: "flex",
                    flexDirection: "column"
                }}
            >
                <ReadCanvasView
                    elements={elements}
                    tableGroups={tableGroups}
                    desktopReservationsCollapsible={desktopReservationsCollapsible}
                />
            </Box>
        </FloorplanContextProvider>
    );
};

export default FloorPlanContainer;
